import { UseFormReturn } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { HighlightableSelect } from '@/components/ui/highlightable-select';
import { HighlightableInput } from '@/components/ui/highlightable-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ResponsiveImageUpload } from '../shared/responsive-image-upload';
import { useState, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { uploadSiteSurveyPhoto } from '../shared/site-survey-photo-upload';

interface ElectricalSupplyProps {
  form: UseFormReturn<any>;
  installationId?: number;
  surveyId?: number;
  onPhotoUploaded?: () => void;
}

export function ElectricalSupply({
  form,
  installationId,
  surveyId,
  onPhotoUploaded,
}: ElectricalSupplyProps) {
  const [uploadingImages, setUploadingImages] = useState<Record<string, boolean>>({});
  const { toast } = useToast();
  const fileInputRefs = {
    photoIncomer: useRef<HTMLInputElement>(null),
    photoMeter: useRef<HTMLInputElement>(null),
    photoMainDb: useRef<HTMLInputElement>(null),
    photoOther: useRef<HTMLInputElement>(null),
  };

  const isMobileDevice = () => {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 2)
    );
  };

  const handleImageUpload = async (field: string, file: File) => {
    setUploadingImages((prev) => ({ ...prev, [field]: true }));

    try {
      await uploadSiteSurveyPhoto({
        form,
        field,
        file,
        surveyId,
        installationId,
        customerId: form.getValues('customerId'),
        description: `Electrical ${field} photo`,
        toast,
        onPhotoUploaded,
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description:
          error instanceof Error ? error.message : 'Failed to upload image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploadingImages((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleFileSelect = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(field, file);
    }
    event.target.value = '';
  };

  const handleCameraCapture = (field: string) => {
    const input = fileInputRefs[field as keyof typeof fileInputRefs];
    if (input.current) {
      if (isMobileDevice()) {
        input.current.setAttribute('capture', 'environment');
      } else {
        input.current.removeAttribute('capture');
      }
      input.current.click();
    }
  };

  const triggerFileUpload = (field: string) => {
    const input = fileInputRefs[field as keyof typeof fileInputRefs];
    if (input.current) {
      input.current.removeAttribute('capture');
      input.current.click();
    }
  };

  const renderImageUpload = (fieldName: string, label: string) => {
    return (
      <ResponsiveImageUpload
        form={form}
        fieldName={fieldName}
        label={label}
        isUploading={uploadingImages[fieldName]}
        onCameraCapture={() => handleCameraCapture(fieldName)}
        onFileUpload={() => triggerFileUpload(fieldName)}
        onPhotoDeleted={onPhotoUploaded}
      />
    );
  };

  return (
    <div className="space-y-6">
      {Object.entries(fileInputRefs).map(([key, ref]) => (
        <input
          key={key}
          ref={ref}
          type="file"
          accept="image/*,.heic,.heif"
          style={{ display: 'none' }}
          onChange={handleFileSelect(key)}
        />
      ))}

      {/* Electrical System Information */}
      <Card>
        <CardHeader>
          <CardTitle>Electrical System Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={form.control}
            name="phases"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phases</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value || ''}
                    className="flex flex-row space-x-4"
                  >
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <RadioGroupItem value="ONE" />
                      </FormControl>
                      <FormLabel className="font-normal cursor-pointer">One</FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <RadioGroupItem value="THREE" />
                      </FormControl>
                      <FormLabel className="font-normal cursor-pointer">Three</FormLabel>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="mainFuseSize"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Main Fuse Size</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select fuse size"
                      options={[
                        { value: '60', label: '60' },
                        { value: '63', label: '63' },
                        { value: '80', label: '80' },
                        { value: '100', label: '100' },
                        { value: '200', label: '200' },
                        { value: 'UNKNOWN', label: 'Unknown' },
                      ]}
                      fieldName="mainFuseSize"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="earthingSystem"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Earthing System</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select earthing system"
                      options={[
                        { value: 'TNS', label: 'TNS' },
                        { value: 'TN-CS', label: 'TN-CS' },
                        { value: 'TT', label: 'TT' },
                        { value: 'UNKNOWN', label: 'Unknown' },
                      ]}
                      fieldName="earthingSystem"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* AC Demand Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="existingDemand"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Existing Demand (Amps)</FormLabel>
                  <FormControl>
                    <HighlightableInput
                      {...field}
                      type="number"
                      placeholder="e.g., 60"
                      min="0"
                      step="0.1"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="font-mono"
                      hint="Enter the current electrical demand in Amps"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="newDemand"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Demand (Amps)</FormLabel>
                  <FormControl>
                    <HighlightableInput
                      {...field}
                      type="number"
                      placeholder="e.g., 80"
                      min="0"
                      step="0.1"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="font-mono"
                      hint="Enter the projected demand after solar installation in Amps"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="bondingWater"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bonding Water</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select bonding status"
                      options={[
                        { value: 'YES', label: 'Yes' },
                        { value: 'N/A', label: 'N/A' },
                        { value: 'REQUIRED', label: 'Required' },
                      ]}
                      fieldName="bondingWater"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="bondingGas"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bonding Gas/Oil</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select bonding status"
                      options={[
                        { value: 'YES', label: 'Yes' },
                        { value: 'N/A', label: 'N/A' },
                        { value: 'REQUIRED', label: 'Required' },
                      ]}
                      fieldName="bondingGas"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="acSpdPresent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>AC SPD Present</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select SPD status"
                      options={[
                        { value: 'YES', label: 'Yes' },
                        { value: 'NO', label: 'No' },
                        { value: 'REQUIRED', label: 'Required' },
                      ]}
                      fieldName="acSpdPresent"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="eicr"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>EICR</FormLabel>
                  <FormControl>
                    <HighlightableSelect
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      placeholder="Select EICR status"
                      options={[
                        { value: 'NOT REQUIRED', label: 'Not Required' },
                        { value: 'REQUIRED', label: 'Required' },
                      ]}
                      fieldName="eicr"
                      form={form}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="spaceAvailableForPvDb"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Space Available for PV DB</FormLabel>
                <FormControl>
                  <HighlightableSelect
                    value={field.value || ''}
                    onValueChange={field.onChange}
                    placeholder="Select availability"
                    options={[
                      { value: 'YES', label: 'Yes' },
                      { value: 'NO', label: 'No' },
                    ]}
                    fieldName="spaceAvailableForPvDb"
                    form={form}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      {/* Electrical Photos */}
      <Card>
        <CardHeader>
          <CardTitle>Electrical Photos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderImageUpload('photoIncomer', 'Image: Incomer')}
            {renderImageUpload('photoMeter', 'Image: Meter')}
            {renderImageUpload('photoMainDb', 'Image: Main DB')}
            {renderImageUpload('photoOther', 'Image: Other')}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
